import React from "react";
import { useTranslation } from "react-i18next";

import { AsyncImg } from "../../../components/async-img/AsyncImg";
import { Heading } from "../../../components/heading/Heading";
import { ViewSection } from "../../../components/view-section/ViewSection";
import { SubTitle } from "../../../components/sub-title/SubTitle";
import { CustomAddShopCart } from "../banner-section/ProductOasisAcademyBannerSection";
import { HeroSectionOption } from "../../../components/hero-section/HeroSection";
import { ServiceOfferAvailability, ServicePeriodCode, ServiceProductCode } from "../../../graphql/schema";
import { IconTag } from "../../../components/icon/IconTag";
import { CurrencySign, formatPrice } from "../../../components/price/Price";
import { Separator } from "../../../components/separator/Separator";
import awenturesImage from "./assets/oasisAcademyIntro.jpeg";

import styles from "./product-oasis-academy-intro-section.module.scss";

export interface ProductOasisAcademyIntroSectionProps {
  productCode: keyof typeof ServiceProductCode;
  product?: HeroSectionOption & {
    availability?: ServiceOfferAvailability;
    upgradableSubscriptions?: { id?: string }[];
    comingSoon?: boolean;
    period?: { code?: ServicePeriodCode };
  };
}

export const ProductOasisAcademyIntroSection: React.FC<ProductOasisAcademyIntroSectionProps> = ({
  productCode,
  product,
}) => {
  // access translation keys
  const { t } = useTranslation();

  return (
    <ViewSection
      withBackground
      image={<AsyncImg src={awenturesImage} className={styles.image} height={"1024px"} />}
      gap="GAP_40"
      gutter={160}
    >
      <Heading className={styles.title} gutter={8} level={4}>
        {t("Oasis is a unique fusion of Elliott Wave Analysis and Smart Money Concepts (SMC)")}
      </Heading>
      <SubTitle withBottomLine>{t("oasis-academic.com")}</SubTitle>
      <div className={styles.content}>
        <p>{t("Oasis Academy gives you more than just trading education, it provides you with:")}</p>

        <ul className={styles["dash-list"]}>
          <li>{t("A long-term roadmap to build a clear strategic vision of the markets.")}</li>
          <li>
            {t(
              "Insights into the intentions of market makers and major financial institutions to deeply understand price movements.",
            )}
          </li>
          <li>
            {t(
              "The ability to uncover the tricks of market makers and institutions so you can avoid common trader traps.",
            )}
          </li>
          <li>
            {t("Highly accurate entry and exit points that allow you to trade with confidence and professionalism.")}
          </li>
        </ul>
        <p>
          {t(
            "All of this is supported by real market applications, ready-to-use analytical tools, live practical sessions, and a professional trading community that guides you step by step toward mastery.",
          )}
        </p>
        {product && !product.comingSoon && (
          <div className={styles["cart-wrapper"]}>
            <CustomAddShopCart
              productCode={productCode}
              {...product}
              activeSubscriptionId={product?.upgradableSubscriptions?.[0]?.id}
              periodCode={product?.period?.code}
              fullPrice={product?.fullPriceNext ?? product?.fullPrice}
              discountedPrice={product?.fullPriceNext !== product?.fullPrice ? product?.fullPrice : null}
              className={styles["cart-button"]}
              labelClassName={styles["cart-button-label"]}
            />

            {product?.fullPriceNext !== product?.fullPrice &&
              product.availability === ServiceOfferAvailability.AVAILABLE_FOR_PURCHASE && (
                <span className={styles["discount-label"]}>
                  <IconTag />
                  {t("Save {{price}} now", {
                    price: `${formatPrice(
                      Number(product.fullPriceNext) - Number(product.fullPrice),
                      product.currencyCode,
                      true,
                    )}${CurrencySign[product.currencyCode]}`,
                  })}
                </span>
              )}
          </div>
        )}
      </div>

      <Separator className={styles["bottom-separator"]} type="SECONDARY" position="BOTTOM" />
    </ViewSection>
  );
};
